import { useCallback, useEffect, useState } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import { ApiError } from '@/lib/api/client'
import {
  getSettingsProjectTypes,
  createSettingsProjectType,
  updateSettingsProjectType,
  deleteSettingsProjectType,
  reorderSettingsProjectTypes,
  getOrgLanguages,
  updateOrgLanguages,
  type ProjectTypeSettingDto,
  type TranslationDto,
} from '@/lib/api/v2/settings'

// ── Curated emoji list for home/renovation context ────────────────────────────
const CURATED_EMOJIS = [
  '🏠', '🏡', '🏗️', '🏢', '🛋️', '🛁', '🚿', '🍳',
  '🪟', '🚪', '🪴', '💡', '🔧', '🪛', '🔨', '🏗️',
  '🛏️', '🪞', '🚽', '🏊',
]

const LANGUAGE_LABELS: Record<string, string> = {
  'fr-BE': 'Français (Belgique)',
  'nl-BE': 'Nederlands (België)',
  'en-GB': 'English (UK)',
}

const ALL_LANGUAGES = ['fr-BE', 'nl-BE', 'en-GB']

// ── Emoji picker ─────────────────────────────────────────────────────────────

interface EmojiPickerProps {
  value: string
  onChange: (emoji: string) => void
}

function EmojiPicker({ value, onChange }: EmojiPickerProps) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-10 gap-1">
        {CURATED_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onChange(emoji)}
            className={`text-xl p-1 rounded hover:bg-muted transition-colors ${
              value === emoji ? 'bg-primary/10 ring-1 ring-primary' : ''
            }`}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Ou saisir :</Label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="🏠 ou texte"
          className="h-8 text-sm"
          maxLength={4}
        />
      </div>
    </div>
  )
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────

interface TypeDialogProps {
  open: boolean
  onClose: () => void
  onSave: (icon: string | null, translations: TranslationDto[], isEnabled: boolean) => Promise<void>
  initial?: ProjectTypeSettingDto | null
  supportedLanguages: string[]
  isSaving: boolean
}

function TypeDialog({ open, onClose, onSave, initial, supportedLanguages, isSaving }: TypeDialogProps) {
  const [icon, setIcon] = useState(initial?.icon ?? '')
  const [isEnabled, setIsEnabled] = useState(initial?.isEnabled ?? true)
  const [labels, setLabels] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    for (const lang of supportedLanguages) {
      init[lang] = initial?.translations.find((t) => t.language === lang)?.label ?? ''
    }
    return init
  })

  const handleSave = async () => {
    const translations: TranslationDto[] = supportedLanguages
      .filter((lang) => labels[lang]?.trim())
      .map((lang) => ({ language: lang, label: labels[lang].trim() }))

    if (translations.length === 0) {
      showErrorToast('Au moins un libellé est requis.')
      return
    }

    await onSave(icon.trim() || null, translations, isEnabled)
  }

  const isEdit = !!initial

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le type de projet' : 'Nouveau type de projet'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Icon picker */}
          <div className="space-y-2">
            <Label>Icône</Label>
            <EmojiPicker value={icon} onChange={setIcon} />
          </div>

          <Separator />

          {/* Labels per language */}
          <div className="space-y-3">
            <Label>Libellés</Label>
            {supportedLanguages.map((lang) => (
              <div key={lang} className="space-y-1">
                <Label className="text-xs text-muted-foreground">{LANGUAGE_LABELS[lang] ?? lang}</Label>
                <Input
                  value={labels[lang] ?? ''}
                  onChange={(e) => setLabels((prev) => ({ ...prev, [lang]: e.target.value }))}
                  placeholder={`Libellé en ${LANGUAGE_LABELS[lang] ?? lang}`}
                />
              </div>
            ))}
          </div>

          {/* Enabled toggle (edit only) */}
          {isEdit && (
            <>
              <Separator />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is-enabled"
                  checked={isEnabled}
                  onCheckedChange={(v) => setIsEnabled(Boolean(v))}
                />
                <Label htmlFor="is-enabled" className="cursor-pointer">
                  Type activé (visible dans la création de projet)
                </Label>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Languages card ────────────────────────────────────────────────────────────

interface LanguagesCardProps {
  supportedLanguages: string[]
  onSave: (langs: string[]) => Promise<void>
  isSaving: boolean
}

function LanguagesCard({ supportedLanguages, onSave, isSaving }: LanguagesCardProps) {
  const [selected, setSelected] = useState<string[]>(supportedLanguages)

  useEffect(() => {
    setSelected(supportedLanguages)
  }, [supportedLanguages])

  const toggle = (lang: string) => {
    setSelected((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    )
  }

  const hasChanged = JSON.stringify([...selected].sort()) !== JSON.stringify([...supportedLanguages].sort())

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div>
        <h3 className="font-medium text-sm">Langues de l'organisation</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Les libellés seront demandés pour chaque langue activée.
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        {ALL_LANGUAGES.map((lang) => (
          <label key={lang} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={selected.includes(lang)}
              onCheckedChange={() => toggle(lang)}
            />
            <span className="text-sm">{LANGUAGE_LABELS[lang]}</span>
          </label>
        ))}
      </div>
      {hasChanged && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => onSave(selected)}
            disabled={isSaving || selected.length === 0}
          >
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function ProjectTypesSettings() {
  const [types, setTypes] = useState<ProjectTypeSettingDto[]>([])
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>(['fr-BE'])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingType, setEditingType] = useState<ProjectTypeSettingDto | null>(null)
  const [isSavingType, setIsSavingType] = useState(false)

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [isSavingLanguages, setIsSavingLanguages] = useState(false)

  const load = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const [typesData, langData] = await Promise.all([
        getSettingsProjectTypes(),
        getOrgLanguages(),
      ])
      setTypes(typesData)
      setSupportedLanguages(langData.supportedLanguages)
    } catch {
      setError('Impossible de charger les paramètres.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleOpenCreate = () => {
    setEditingType(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (type: ProjectTypeSettingDto) => {
    setEditingType(type)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingType(null)
  }

  const handleSaveType = async (
    icon: string | null,
    translations: TranslationDto[],
    isEnabled: boolean
  ) => {
    setIsSavingType(true)
    try {
      if (editingType) {
        const updated = await updateSettingsProjectType(editingType.id, { icon, isEnabled, translations })
        setTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
        showSuccessToast('Type mis à jour.')
      } else {
        const created = await createSettingsProjectType({ icon, translations })
        setTypes((prev) => [...prev, created])
        showSuccessToast('Type créé.')
      }
      handleCloseDialog()
    } catch {
      showErrorToast('Erreur lors de l\'enregistrement.')
    } finally {
      setIsSavingType(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setIsDeleting(true)
    try {
      await deleteSettingsProjectType(deleteId)
      setTypes((prev) => prev.filter((t) => t.id !== deleteId))
      showSuccessToast('Type supprimé.')
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TYPE_IN_USE') {
        showErrorToast('Ce type est utilisé par des projets existants.')
      } else {
        showErrorToast('Erreur lors de la suppression.')
      }
    } finally {
      setIsDeleting(false)
      setDeleteId(null)
    }
  }

  const moveType = async (index: number, direction: -1 | 1) => {
    const newTypes = [...types]
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= newTypes.length) return

    ;[newTypes[index], newTypes[targetIndex]] = [newTypes[targetIndex], newTypes[index]]

    const reordered = newTypes.map((t, i) => ({ ...t, sortOrder: i + 1 }))
    setTypes(reordered)

    try {
      await reorderSettingsProjectTypes(reordered.map((t) => ({ id: t.id, sortOrder: t.sortOrder })))
    } catch {
      showErrorToast('Erreur lors de la réorganisation.')
      load()
    }
  }

  const handleSaveLanguages = async (langs: string[]) => {
    setIsSavingLanguages(true)
    try {
      const result = await updateOrgLanguages(langs)
      setSupportedLanguages(result.supportedLanguages)
      showSuccessToast('Langues mises à jour.')
    } catch {
      showErrorToast('Erreur lors de la mise à jour des langues.')
    } finally {
      setIsSavingLanguages(false)
    }
  }

  const getLabel = (type: ProjectTypeSettingDto) => {
    return (
      type.translations.find((t) => t.language === 'fr-BE')?.label ??
      type.translations[0]?.label ??
      type.code
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Languages card */}
      <LanguagesCard
        supportedLanguages={supportedLanguages}
        onSave={handleSaveLanguages}
        isSaving={isSavingLanguages}
      />

      {/* Project types table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-sm">Types de projet</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Définissez les types disponibles lors de la création d'un projet.
            </p>
          </div>
          <Button size="sm" onClick={handleOpenCreate} data-testid="settings-project-types-add-btn">
            <Plus className="size-4" />
            Ajouter
          </Button>
        </div>

        {types.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            Aucun type de projet configuré.
          </div>
        ) : (
          <div className="rounded-lg border divide-y">
            {types.map((type, index) => (
              <div key={type.id} className="flex items-center gap-3 px-4 py-3">
                {/* Sort controls */}
                <div className="flex flex-col gap-0.5 text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => moveType(index, -1)}
                    disabled={index === 0}
                    className="hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveType(index, 1)}
                    disabled={index === types.length - 1}
                    className="hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronDown className="size-3.5" />
                  </button>
                </div>

                <GripVertical className="size-4 text-muted-foreground/40 shrink-0" />

                {/* Icon */}
                <span className="text-lg w-7 text-center shrink-0">{type.icon ?? '📁'}</span>

                {/* Label */}
                <span className="flex-1 text-sm font-medium">{getLabel(type)}</span>

                {/* Status badge */}
                <Badge variant={type.isEnabled ? 'default' : 'outline'} className="text-xs">
                  {type.isEnabled ? 'Activé' : 'Désactivé'}
                </Badge>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => handleOpenEdit(type)}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteId(type.id)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/edit dialog — key forces remount so useState initializers re-run */}
      <TypeDialog
        key={editingType?.id ?? `new-${dialogOpen}`}
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveType}
        initial={editingType}
        supportedLanguages={supportedLanguages}
        isSaving={isSavingType}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce type ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les projets existants avec ce type conserveront leur code
              mais le libellé ne sera plus résolu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Suppression…' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
